/**
 * 客戶列表表格元件 (CustomerTable)
 * 
 * 職責：
 * - 顯示完整的客戶列表表格 UI
 * - 包含客戶資訊、聯絡方式、會員類別、折扣、租借統計、標籤、狀態、操作按鈕
 * - 提供快速撥打電話、寄送 Email 功能
 * - 處理空狀態顯示
 * 
 * @module CustomerTable
 */

import { useState, useEffect, useRef } from 'react';
import type { Customer } from '../../../../modules/customer/types';
import { levelConfig, statusConfig, MAX_VISIBLE_TAGS } from '../../../../modules/customer/constants';
import { getDiscountColor, formatPhoneForCall, createEmailSubject, formatDiscountText } from '../../../../modules/customer/helpers';
import { getIdentityStatusConfig } from '../../../../modules/identity/helpers';
import type { IdentityDocumentSummary } from '../../../../modules/identity/types';

/**
 * CustomerTable Props 介面
 */
interface CustomerTableProps {
  /** 客戶列表資料 */
  customers: Customer[];
  
  /** 身分證件狀態映射 */
  identityStatuses?: Map<string, IdentityDocumentSummary>;
  
  /** 變更密碼回調 */
  onResetPassword: (customer: Customer) => void;
  
  /** 編輯客戶回調 */
  onEdit: (customer: Customer) => void;
  
  /** 查看訂單回調 */
  onViewOrders: (customer: Customer) => void;
}

const PAGE_SIZE = 25;

/**
 * 客戶列表表格元件
 */
export function CustomerTable({
  customers,
  identityStatuses,
  onResetPassword,
  onEdit,
  onViewOrders
}: CustomerTableProps): JSX.Element {
  const [currentPage, setCurrentPage] = useState(1);
  // 計時：記錄 table 首次收到資料後的 render 完成時間
  const firstDataRef = useRef(false);

  useEffect(() => {
    if (customers.length > 0 && !firstDataRef.current) {
      firstDataRef.current = true;
      console.log(`[PERF] ⑧ CustomerTable 首次 render 完成（customers.length=${customers.length}）`);
      // 計算 avatar 圖片比例
      const noAvatarCount = customers.filter(c => !c.avatar_url).length;
      console.log(`[PERF] ⑨ 無 avatar_url（使用 AI 圖 API）: ${noAvatarCount}/${customers.length} 筆，可能觸發 ${noAvatarCount} 個外部圖片請求`);
    }
  }, [customers]);

  // 當篩選結果改變時重置頁碼
  const totalPages = Math.max(1, Math.ceil(customers.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  if (safePage !== currentPage) {
    setCurrentPage(safePage);
  }

  const startIdx = (safePage - 1) * PAGE_SIZE;
  const endIdx = startIdx + PAGE_SIZE;
  const pagedCustomers = customers.slice(startIdx, endIdx);

  // ==================== 空狀態 ====================
  
  if (customers.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ri-user-search-line text-4xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">沒有符合條件的客戶</h3>
        <p className="text-sm text-gray-500">請調整篩選條件或新增客戶</p>
      </div>
    );
  }

  // 產生頁碼按鈕
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push('...');
      const start = Math.max(2, safePage - 1);
      const end = Math.min(totalPages - 1, safePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (safePage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  // ==================== 表格渲染 ====================
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-gray-200">
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                客戶資訊
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                聯絡方式
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                會員類別
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                折扣
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                租借統計
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                標籤
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                證件狀態
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                狀態
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider whitespace-nowrap">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pagedCustomers.map((customer) => {
              const levelCfg = levelConfig[customer.member_level];
              const statusCfg = statusConfig[customer.status];
              const visibleTags = customer.tags?.slice(0, MAX_VISIBLE_TAGS) || [];
              const remainingTagsCount = (customer.tags?.length || 0) - MAX_VISIBLE_TAGS;

              return (
                <tr key={customer.id} className="hover:bg-gray-50 transition-colors">
                  {/* 客戶資訊 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <img
                        src={customer.avatar}
                        alt={customer.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800">{customer.name}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {/* 客戶類型標籤 */}
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            (customer as any).customer_type === 'company'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {(customer as any).customer_type === 'company' ? (
                              <><i className="ri-building-line text-[10px]"></i> 公司</>
                            ) : (
                              <><i className="ri-user-line text-[10px]"></i> 個人</>
                            )}
                          </span>
                          {customer.company && (
                            <span className="text-xs text-gray-500">{customer.company}</span>
                          )}
                        </div>
                        {/* 身分證字號 / 統一編號顯示 */}
                        {(customer as any).identity_number && (customer as any).customer_type !== 'company' && (
                          <div className="text-[10px] text-emerald-600 mt-0.5 font-mono">
                            <i className="ri-id-card-line mr-1"></i>
                            身分證字號: {(customer as any).identity_number}
                          </div>
                        )}
                        {customer.tax_id && (
                          <div className="text-[10px] text-blue-600 mt-0.5 font-mono">
                            <i className="ri-bank-card-line mr-1"></i>
                            統一編號: {customer.tax_id}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* 聯絡方式 */}
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {customer.phone && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">{customer.phone}</span>
                          <a
                            href={`tel:${formatPhoneForCall(customer.phone)}`}
                            className="w-6 h-6 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded transition-colors"
                            title="撥打電話"
                          >
                            <i className="ri-phone-line text-sm"></i>
                          </a>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">{customer.email}</span>
                          <a
                            href={`mailto:${customer.email}?subject=${createEmailSubject(customer.name)}`}
                            className="w-6 h-6 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded transition-colors"
                            title="寄送 Email"
                          >
                            <i className="ri-mail-line text-sm"></i>
                          </a>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* 會員類別 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${levelCfg.color} ${levelCfg.bgColor}`}>
                      <span>{levelCfg.icon}</span>
                      <span>{levelCfg.label}</span>
                    </span>
                  </td>

                  {/* 折扣 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm ${getDiscountColor(customer.discount)}`}>
                      {formatDiscountText(customer.discount)}
                    </span>
                  </td>

                  {/* 租借統計 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="text-gray-700">
                        <span className="font-semibold">{customer.total_orders}</span> 筆訂單
                      </div>
                      <div className="text-gray-500 text-xs">
                        NT$ {customer.total_spent.toLocaleString('zh-TW')}
                      </div>
                    </div>
                  </td>

                  {/* 標籤 */}
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {visibleTags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-block px-2 py-1 bg-teal-100 text-teal-700 text-xs rounded-full whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                      {remainingTagsCount > 0 && (
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full whitespace-nowrap">
                          +{remainingTagsCount}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* 證件狀態 */}
                <td className="px-6 py-4 whitespace-nowrap">
                  {(() => {
                    const identitySummary = identityStatuses?.get(customer.id);
                    if (!identitySummary) {
                      return (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">
                          <i className="ri-question-line"></i>
                          暫時無法確認
                        </span>
                      );
                    }
                    const config = getIdentityStatusConfig(identitySummary?.overallStatus);
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
                        <i className={config.icon}></i>
                        {config.label}
                      </span>
                    );
                  })()}
                </td>

                {/* 狀態 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusCfg.color} ${statusCfg.bgColor}`}>
                      {statusCfg.label}
                    </span>
                  </td>

                  {/* 操作 */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {customer.auth_user_id && (
                        <button
                          onClick={() => onResetPassword(customer)}
                          className="w-8 h-8 flex items-center justify-center text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                          title="變更密碼"
                        >
                          <i className="ri-lock-password-line text-base"></i>
                        </button>
                      )}
                      <button
                        onClick={() => onEdit(customer)}
                        className="w-8 h-8 flex items-center justify-center text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                        title="編輯"
                      >
                        <i className="ri-edit-line text-base"></i>
                      </button>
                      <button
                        onClick={() => onViewOrders(customer)}
                        className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        title="查看訂單"
                      >
                        <i className="ri-file-list-3-line text-base"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 分頁控制列 */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
        <p className="text-sm text-gray-600">
          共 <span className="font-semibold">{customers.length}</span> 筆，顯示第{' '}
          <span className="font-semibold">{startIdx + 1}</span> –{' '}
          <span className="font-semibold">{Math.min(endIdx, customers.length)}</span> 筆
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          {getPageNumbers().map((p, idx) =>
            typeof p === 'string' ? (
              <span key={`dot-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
            ) : (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`px-3 py-1.5 text-sm rounded-lg border cursor-pointer whitespace-nowrap ${
                  p === safePage
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'border-gray-300 bg-white hover:bg-gray-100 text-gray-700'
                }`}
              >
                {p}
              </button>
            )
          )}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
